import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiTruck, FiClock, FiCheck } from 'react-icons/fi';
import api, { shippingAPI } from '../../services/api';
import StepProgress from '../../components/StepProgress';

const ShippingOptions = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { shipmentData, fromStep } = location.state || {};

  const [rates, setRates] = useState([]);
  const [selectedRate, setSelectedRate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLiveRates, setIsLiveRates] = useState(false);
  const [easypostShipmentId, setEasypostShipmentId] = useState(null);

  useEffect(() => {
    if (!shipmentData) {
      navigate('/customer/requests/new?type=ship');
      return;
    }

    fetchShippingRates();
  }, [shipmentData]);

  const fetchShippingRates = async () => {
    try {
      setLoading(true);
      setError(null);

      // If we have a request ID, try live EasyPost rates via the backend
      if (shipmentData.requestId) {
        try {
          const liveResult = await shippingAPI.getShippingRates(
            shipmentData.requestId,
            shipmentData.shippingMethod
          );

          if (liveResult && liveResult.rates && liveResult.rates.length > 0) {
            const normalizedRates = liveResult.rates.map(rate => ({
              id: rate.id,
              name: rate.service_name || `${rate.carrier} ${rate.service}`,
              carrier: rate.carrier,
              service: rate.service,
              estimatedCost: rate.cost,
              transitDays: rate.transit_days || 'N/A',
              estimatedDeliveryDate: rate.estimated_delivery_date
            }));

            setRates(normalizedRates);
            setIsLiveRates(!liveResult.demo);
            setEasypostShipmentId(liveResult.easypost_shipment_id || null);

            if (normalizedRates.length > 0) {
              const cheapest = normalizedRates.reduce((min, rate) =>
                rate.estimatedCost < min.estimatedCost ? rate : min);
              setSelectedRate(cheapest);
            }
            return;
          }
        } catch {
          // Fall through to demo rates
        }
      }

      // Fallback: demo rates
      const totalWeight = shipmentData.lineItems?.reduce((sum, item) =>
        sum + (parseFloat(item.weight || 5) * parseInt(item.quantity || 1)), 0) || 25;
      const totalVolume = shipmentData.lineItems?.reduce((sum, item) =>
        sum + (parseFloat(item.volume || 1) * parseInt(item.quantity || 1)), 0) || 5;

      const method = totalWeight > 150 || totalVolume > 15 ? 'freight' : 'courier';

      const items = shipmentData.lineItems?.map(item => ({
        sku: item.productSku,
        qty: item.quantity,
        weight: item.weight || 5,
        volume: item.volume || 1
      })) || [];

      const options = {
        liftgateRequired: shipmentData.liftgateRequired || false,
        whiteGloveService: shipmentData.whiteGloveService || false,
        insideDelivery: shipmentData.insideDelivery || false
      };

      const response = await api.post('/shipping/rates/demo', {
        method,
        items,
        options
      });

      setRates(response.data);
      setIsLiveRates(false);

      if (response.data.length > 0) {
        const cheapest = response.data.reduce((min, rate) =>
          rate.estimatedCost < min.estimatedCost ? rate : min);
        setSelectedRate(cheapest);
      }

    } catch (err) {
      console.error('Error fetching shipping rates:', err);
      setError('Failed to load shipping rates. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRateSelect = (rate) => {
    setSelectedRate(rate);
  };

  const handleContinue = () => {
    if (!selectedRate) return;

    const updatedShipmentData = {
      ...shipmentData,
      selectedShippingRate: selectedRate,
      shippingCost: selectedRate.estimatedCost,
      easypostShipmentId: easypostShipmentId,
      easypostRateId: isLiveRates ? selectedRate.id : null
    };

    // Navigate to review step (step 4)
    navigate('/customer/requests/new', {
      state: {
        shipmentData: updatedShipmentData,
        step: 4,
        returnFromShipping: true
      }
    });
  };

  const handleBack = () => {
    // Navigate back to details step (step 2)
    navigate('/customer/requests/new', {
      state: {
        shipmentData,
        step: 2,
        returnFromShipping: false
      }
    });
  };

  if (!shipmentData) {
    return null; // Will redirect
  }

  if (loading) {
    return (
      <div className="py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading shipping options...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-red-600 mb-4">
              <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Shipping Options</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <div className="space-x-3">
              <button
                onClick={fetchShippingRates}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
              >
                Try Again
              </button>
              <button
                onClick={handleBack}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Define step mappings by request type (same as CreateRequest)
  const stepsByType = {
    shipping: ['Type', 'Details', 'Shipping Options', 'Review'],
    asn: ['Type', 'Details', 'Review'],
    kit: ['Type', 'Details', 'Review']
  };

  // Get step configuration
  const getStepConfig = () => {
    const requestType = shipmentData?.requestType;
    
    if (!requestType) {
      return { steps: [], totalSteps: 0 };
    }
    
    const typeMapping = {
      'ship': 'shipping',
      'incoming': 'asn',
      'kit_build': 'kit'
    };
    
    const stepConfigKey = typeMapping[requestType] || 'asn';
    const stepLabels = stepsByType[stepConfigKey];
    const steps = stepLabels.map((label, index) => ({
      id: index + 1,
      name: label,
      shortName: label
    }));
    
    return {
      steps,
      totalSteps: steps.length
    };
  };

  const stepConfig = getStepConfig();
  const currentStep = 3; // We're on step 3 (Shipping Options)

  return (
    <div className="py-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">Choose Shipping Option</h1>
          <p className="mt-2 text-sm text-gray-700">
            Select the best shipping option for your delivery
          </p>
        </div>

        {/* Progress Steps */}
        {stepConfig.steps.length > 0 && (
          <StepProgress steps={stepConfig.steps} currentStep={currentStep} />
        )}

        {/* Shipment Summary */}
        <div className="bg-gray-50 rounded-lg p-4 mb-8">
          <h2 className="text-lg font-medium text-gray-900 mb-3">Shipment Summary</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Items:</span>
              <span className="ml-2 text-gray-600">{shipmentData.lineItems?.length || 0} products</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Total Weight:</span>
              <span className="ml-2 text-gray-600">
                {shipmentData.lineItems?.reduce((sum, item) => 
                  sum + (parseFloat(item.weight || 5) * parseInt(item.quantity || 1)), 0).toFixed(1) || '25.0'} lbs
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Destination:</span>
              <span className="ml-2 text-gray-600">
                {shipmentData.destinationAddress?.city || 'Not specified'}, {shipmentData.destinationAddress?.state || ''}
              </span>
            </div>
          </div>
        </div>

        {/* Shipping Rate Options */}
        <div className="space-y-4 mb-8">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">Available Shipping Options</h2>
            {isLiveRates && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                Live Rates
              </span>
            )}
          </div>

          {rates.map((rate) => (
            <div
              key={rate.id}
              className={`border rounded-lg p-6 cursor-pointer transition-all ${
                selectedRate?.id === rate.id
                  ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200'
                  : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
              }`}
              onClick={() => handleRateSelect(rate)}
            >
              <div className="flex items-center justify-between w-full">
                {/* Rate Info */}
                <div className="flex items-center space-x-3 flex-1">
                  <FiTruck className={`h-5 w-5 ${selectedRate?.id === rate.id ? 'text-indigo-600' : 'text-gray-400'}`} />
                  <div className="flex-1">
                    <h3 className={`text-lg font-medium ${selectedRate?.id === rate.id ? 'text-indigo-900' : 'text-gray-900'}`}>
                      {rate.name}
                    </h3>
                    <div className="flex items-center space-x-4 mt-1">
                      <div className="flex items-center space-x-1">
                        <FiClock className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-600">
                          {rate.transitDays}{typeof rate.transitDays === 'number' ? ' days' : ' business days'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <span className="text-sm text-gray-600">{rate.carrier}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Price - Right aligned */}
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <span className={`text-2xl font-bold ${selectedRate?.id === rate.id ? 'text-indigo-900' : 'text-gray-900'}`}>
                      ${(rate.estimatedCost || rate.cost || 0).toFixed(2)}
                    </span>
                    <p className="text-sm text-gray-500">{isLiveRates ? 'live rate' : 'estimated'}</p>
                  </div>

                  {/* Selection Indicator */}
                  {selectedRate?.id === rate.id && (
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 bg-indigo-600 rounded-full flex items-center justify-center">
                        <FiCheck className="h-5 w-5 text-white" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Special Services Summary */}
        {(shipmentData.liftgateRequired || shipmentData.whiteGloveService || shipmentData.insideDelivery) && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
            <h3 className="text-sm font-medium text-blue-900 mb-2">Additional Services Included:</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              {shipmentData.liftgateRequired && <li>• Liftgate service</li>}
              {shipmentData.whiteGloveService && <li>• White glove service</li>}
              {shipmentData.insideDelivery && <li>• Inside delivery</li>}
            </ul>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Back to Details
          </button>
          
          <button
            type="button"
            onClick={handleContinue}
            disabled={!selectedRate}
            className={`inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
              selectedRate
                ? 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            Continue to Review
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShippingOptions; 